import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  DeleteDocumentError,
  DeleteDocumentModal,
  DocumentList,
  DocumentViewer,
  formatBytes,
  TextWithEllipsis,
  TitleValue,
  useAppUser,
} from '@bechtel/client-common';
import {
  DELETE_WORK_PACKAGE_ATTACHMENT,
  GET_WORKPACKAGE_BOOK_BUILDER_BY_IDS,
  GET_WORKPACKAGE_DOCUMENTS_BY_IDS,
} from '@bechtel/client-gql';
import {
  Box,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetHeader,
  SideSheetSize,
  Spinner,
  SpinnerSize,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  Text,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  Attachment,
  AttachmentType,
  CrewMember,
  DeleteWorkPackageAttachmentError,
  DeleteWorkPackageAttachmentInput,
  DeleteWorkPackageAttachmentPayload,
  IsoDocument,
  WorkPackage,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { find, get, groupBy, map } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { TabButtonStyled, TabStyled } from '~/detail/styled';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { useWorkPackage } from '../context';
import { DrawingList } from './drawing-list';
import {
  DocumentHeaderStyled,
  InfoContainerStyled,
  SideSheetContentColStyled,
  SideSheetDocumentHeaderStyled,
  SubHeaderStyled,
  TabListStyled,
} from './styled';

export interface DocumentManagerProps {
  workPackageId: string;
  onClose: () => void;
  selectedTag?: string;
  selectedDrawingNo?: string;
}

interface DocumentProps {
  url?: string;
  documentNumber?: string;
  tagNumber?: string;
  documentId?: string;
  fileSize?: string;
  creator?: CrewMember;
}

const getTagNumber = (drawing: IsoDocument) => {
  return drawing.commodity?.materialCommodity ? drawing.commodity?.materialCommodity.tag : drawing.commodity?.weld?.tag;
};

const DocumentManager: FC<DocumentManagerProps> = ({ workPackageId, onClose, selectedTag, selectedDrawingNo }) => {
  const { assignedCrewMembers } = useWorkPackage();
  const { user } = useAppUser();
  const { canUploadDocument, canDeleteDocument } = useWorkPackagePermissions(assignedCrewMembers);
  const [selectedDocument, setSelectedDocument] = useState<DocumentProps | null>();
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [enableDocumentRetry, setEnableDocumentRetry] = useState(false);
  const [deleteDocumentError, setDeleteDocumentError] = useState<DeleteWorkPackageAttachmentError | ApolloError>();
  const { loading, error, data } = useQuery<{ workPackagesByIds: WorkPackage[] }>(GET_WORKPACKAGE_DOCUMENTS_BY_IDS, {
    variables: {
      ids: [workPackageId],
    },
    onCompleted: (data) => {
      if (!selectedDrawingNo) return;
      const drawings = data.workPackagesByIds[0]?.allDocuments;
      const selectedDrawing = find(drawings, (drawing) => drawing?.documentNumber === selectedDrawingNo);
      if (!selectedDrawing) return;
      setSelectedDocument({
        url: selectedDrawing.url,
        documentNumber: selectedDrawing.documentNumber,
        tagNumber: selectedDrawing.commodity?.materialCommodity
          ? selectedDrawing.commodity?.materialCommodity.tag
          : selectedDrawing.commodity?.weld?.tag,
      });
    },
  });

  // tis query is used to refech the book builder URL once the existing book url session is expired.
  const [getBookBuilderData] = useLazyQuery<{ workPackagesByIds: WorkPackage[] }, { ids: string[] }>(
    GET_WORKPACKAGE_BOOK_BUILDER_BY_IDS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data?.workPackagesByIds[0]?.bookUrl) {
          const bookBuilderIndex = docs.map((doc) => doc.id)?.indexOf(bookId);
          if (bookBuilderIndex > -1) {
            docs.splice(bookBuilderIndex, 1);
          }
          const bookDoc = {
            id: bookId,
            fileName: 'Book Builder',
            attachmentType: AttachmentType.DOCUMENT,
            url: data?.workPackagesByIds[0]?.bookUrl,
          };
          setDocs([
            //@ts-ignore book does not contain all document properties
            bookDoc,
            ...docs,
          ]);
          if (selectedDocument?.documentId === bookId) {
            setSelectedDocument({
              url: bookDoc.url,
              documentNumber: bookDoc.fileName,
              documentId: bookDoc.id,
            });
          }
          setEnableDocumentRetry(false);
        }
        setReloadingDoc(false);
      },
      onError: () => {
        setReloadingDoc(false);
      },
    }
  );

  const workPackage = useMemo(() => get(data, 'workPackagesByIds[0]', null), [data]);

  const bookId = `${workPackageId}-book`;
  let documents = workPackage?.attachments ? workPackage?.attachments : [];
  const [docs, setDocs] = useState<Attachment[]>(workPackage?.attachments ? workPackage?.attachments : []);
  const [reloadingDoc, setReloadingDoc] = useState(false);
  const [bookBuilderRetryCount, setBookBuilderRetryCount] = useState(0);
  useEffect(() => {
    if (workPackage?.bookUrl) {
      setDocs([
        { id: bookId, fileName: 'Book Builder', attachmentType: AttachmentType.DOCUMENT, url: workPackage?.bookUrl },
        ...documents,
      ]);
    }
    if (!workPackage?.bookUrl) {
      setDocs([...documents]);
    }
  }, [workPackage]);

  const drawings = workPackage?.allDocuments;
  const groupedDrawings = groupBy(drawings, (drawing) => drawing.documentNumber);
  const drawingNumbers = Object.keys(groupedDrawings);
  const dispatchToast = useToastsDispatch();
  const drawingList = map(drawingNumbers, (drawingNumber) => {
    return {
      drawingNumber: drawingNumber,
      associatedTagNumbers: map(groupedDrawings[drawingNumber], (drawing) => getTagNumber(drawing)),
    };
  });
  const [deleteDocument, { loading: deleteDocumentLoading }] = useMutation<
    { deleteWorkPackageAttachmentPayload: DeleteWorkPackageAttachmentPayload },
    { input: DeleteWorkPackageAttachmentInput }
  >(DELETE_WORK_PACKAGE_ATTACHMENT, {
    onCompleted: (data) => {
      const deleteWorkPackageAttachment = get(data, 'deleteWorkPackageAttachment');
      const deleteDocumentError = get(deleteWorkPackageAttachment, 'error');
      if (deleteDocumentError) {
        setDeleteDocumentError(deleteDocumentError);
      } else {
        const deleteDocumentToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.SUCCESS,
            content: t`Document Deleted`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(deleteDocumentToast);
        setDeleteConfirmed(false);
        setSelectedDocument(null);
        setDeleted(true);
      }
    },
    onError: (error) => {
      setDeleted(false);
      setDeleteDocumentError(error);
    },
  });
  const onDeleteDocument = () => {
    setDeleted(false);
    deleteDocument({
      variables: {
        input: {
          attachmentId: selectedDocument?.documentId ? selectedDocument.documentId : '',
          workPackageId: workPackageId,
        },
      },
    });
  };
  const closeBlockErrorModal = () => {
    setDeleteDocumentError(null);
  };
  useEffect(() => {
    if (deleted && documents?.length == 0) {
      onClose();
    }
  }, [deleted, documents]);

  useEffect(() => {
    if (enableDocumentRetry) {
      setReloadingDoc(true);
      getBookBuilderData({
        variables: {
          ids: [workPackageId],
        },
      });
    }
  }, [enableDocumentRetry]);

  return (
    <SideSheet visible onDismiss={onClose} size={SideSheetSize.LARGE}>
      <SideSheetHeader title={t`Documents`}>
        <SideSheetClose onClick={onClose}></SideSheetClose>
      </SideSheetHeader>
      {loading && (
        <InfoContainerStyled
          css="height:100%;width:100%;"
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
        >
          <Box>
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        </InfoContainerStyled>
      )}
      {error && (
        <Box css="height: 100%;">
          <InfoContainerStyled>
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading documents for this Work Package. Please try again later.</Trans>
              </Text>
            </Box>
          </InfoContainerStyled>
        </Box>
      )}

      {!loading && !error && workPackage && (
        <>
          <SubHeaderStyled>
            <TitleValue title={t`Work Package`} value={workPackage.tag}></TitleValue>
          </SubHeaderStyled>
          <SideSheetBody>
            {!selectedTag && (
              <SideSheetContentColStyled style={{ overflowY: 'hidden' }}>
                <SideSheetDocumentHeaderStyled>
                  <Trans> Documents</Trans> ({drawingNumbers?.length + (docs ? docs?.length : 0)})
                </SideSheetDocumentHeaderStyled>
                <Tabs>
                  <TabListStyled>
                    <TabStyled>
                      {({ selected }: TabProps) => {
                        return (
                          <TabButtonStyled selected={!!selected}>
                            <Text>
                              <Trans>Drawings</Trans> ({drawingNumbers?.length})
                            </Text>
                          </TabButtonStyled>
                        );
                      }}
                    </TabStyled>
                    <TabStyled>
                      {({ selected }: TabProps) => {
                        return (
                          <TabButtonStyled selected={!!selected}>
                            <Text>
                              <Trans>Other Documents</Trans> ({docs?.length})
                            </Text>
                          </TabButtonStyled>
                        );
                      }}
                    </TabStyled>
                  </TabListStyled>
                  <TabPanels>
                    <TabPanel css={{ outline: 'none' }}>
                      <DrawingList
                        drawings={drawingList}
                        onSelect={(drawing) => {
                          setSelectedDocument({
                            url: find(drawings, (document) => document.documentNumber === drawing.drawingNumber)?.url,
                            documentNumber: drawing.drawingNumber,
                            tagNumber: drawing.associatedTagNumbers.join(', '),
                          });
                        }}
                        selectedDrawingNo={selectedDocument?.documentNumber}
                      ></DrawingList>
                    </TabPanel>
                    <TabPanel css={{ outline: 'none' }}>
                      <DocumentList
                        documents={docs}
                        workPackageId={workPackageId}
                        canUploadDocument={canUploadDocument}
                        onSelect={(document) => {
                          setSelectedDocument({
                            url: document.url,
                            documentNumber: document.fileName,
                            documentId: document.id,
                            fileSize: document?.fileSize,
                            creator: document?.creator,
                          });
                        }}
                        bookId={bookId}
                        selectedDocumentId={selectedDocument?.documentId}
                      ></DocumentList>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </SideSheetContentColStyled>
            )}
            <SideSheetContentCol style={{ overflowY: 'hidden' }}>
              {selectedDocument && (
                <DocumentHeaderStyled>
                  <Box mb={{ sm: '400' }}>
                    <Text weight="600" size="500" color="grey.600">
                      {selectedDocument.documentNumber} &nbsp;
                      {selectedDocument.fileSize && `(${formatBytes(parseFloat(selectedDocument.fileSize))})`}
                    </Text>
                  </Box>
                  {selectedDocument?.tagNumber && (
                    <TextWithEllipsis color="grey.600" size="500">
                      <Trans>Tag</Trans>: {selectedTag ? selectedTag : selectedDocument?.tagNumber}
                    </TextWithEllipsis>
                  )}
                </DocumentHeaderStyled>
              )}
              <DocumentViewer
                isBook={selectedDocument?.documentId === bookId}
                retryLoading={reloadingDoc}
                bookBuilderRetryCount={bookBuilderRetryCount}
                enableRetryDocumentLoading={(enable) => {
                  setBookBuilderRetryCount(bookBuilderRetryCount + 1);
                  setEnableDocumentRetry(enable);
                }}
                url={selectedDocument?.url}
                deleting={deleteDocumentLoading}
                canDelete={canDeleteDocument && selectedDocument?.creator?.id === user?.id}
                onDelete={() => {
                  setDeleteConfirmed(true);
                }}
              ></DocumentViewer>
              {deleteConfirmed && (
                <DeleteDocumentModal
                  onDelete={() => onDeleteDocument()}
                  onCancel={() => setDeleteConfirmed(false)}
                  title={'Delete Document'}
                  textContent={`Are you sure you want to delete the document ${selectedDocument?.documentNumber} ?`}
                />
              )}
              {deleteDocumentError && (
                <DeleteDocumentError error={deleteDocumentError} onClose={closeBlockErrorModal} />
              )}
            </SideSheetContentCol>
          </SideSheetBody>
        </>
      )}
    </SideSheet>
  );
};

export default DocumentManager;
